import { useMemo } from 'react';
import type { CommandResult, SyncCommandProvider } from '../types';

/**
 * Simple client-side fuzzy filter for sync providers.
 * Uses case-insensitive substring matching on label + keywords.
 */
function matchesQuery(item: CommandResult, query: string): boolean {
  const q = query.toLowerCase();
  if (!q) return true;
  // Match against label and subtitle
  if (item.label.toLowerCase().includes(q)) return true;
  if (item.subtitle?.toLowerCase().includes(q)) return true;
  // Match against keywords (paths like /admin/document-types)
  if (item.keywords?.some((k) => k.toLowerCase().includes(q))) return true;
  // If query starts with /, also match label without the slash (e.g. "/doc" matches "Documents")
  if (q.startsWith('/')) {
    const stripped = q.slice(1);
    if (stripped && item.label.toLowerCase().includes(stripped)) return true;
  }
  return false;
}

export function useSyncProviderSearch(
  provider: SyncCommandProvider,
  query: string,
  maxResults: number
): CommandResult[] {
  const allItems = useMemo(() => provider.getItems(), [provider]);

  return useMemo(() => {
    if (!query) return allItems.slice(0, maxResults);
    return allItems.filter((item) => matchesQuery(item, query)).slice(0, maxResults);
  }, [allItems, query, maxResults]);
}
